import { getApiUrl } from '@/config/runtime-env';

export const USER_TYPES = {
  STUDENT: "student",
  PARENT: "parent",
  COUNSELOR: "counselor",
  ADMIN: "admin",
} as const;

export const SESSION_TYPES = {
  CHAT: "chat",
  VOICE: "voice",
  VIDEO: "video",
} as const;

export const SESSION_STATUS = {
  ACTIVE: "active",
  ENDED: "ended",
  PENDING: "pending",
} as const;

export const COUNSELOR_STATUS = {
  ONLINE: "online",
  BUSY: "busy",
  OFFLINE: "offline",
} as const;

export const AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  BUSY: "busy",
  OFFLINE: "offline",
} as const;

export const MESSAGE_TYPES = {
  TEXT: "text",
  FILE: "file",
  SYSTEM: "system",
} as const;

export const NOTIFICATION_TYPES = {
  MESSAGE: "message",
  SESSION: "session",
  PROFILE: "profile",
  CALL: "call",
} as const;

const API_BASE_URL = getApiUrl();

export const API_ENDPOINTS = {
  AUTH: {
    // Phone authentication (commented for future use)
    SEND_OTP: `${API_BASE_URL}/auth/send-otp`,
    VERIFY_OTP: `${API_BASE_URL}/auth/verify-otp`,
    // Email authentication (active)
    SEND_EMAIL_OTP: `${API_BASE_URL}/auth/send-email-otp`,
    VERIFY_EMAIL_OTP: `${API_BASE_URL}/auth/verify-email-otp`,
    REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh-token`,
  },
  USER: {
    GET: (id: string) => `${API_BASE_URL}/user/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/user/${id}`,
  },
  PROFILE: {
    COUNSELOR: {
      ME: `${API_BASE_URL}/profile/counselor/me`,
      GET: (userId: string) => `${API_BASE_URL}/profile/counselor/${userId}`,
      UPDATE: (userId: string) => `${API_BASE_URL}/profile/counselor/${userId}`,
      AVAILABILITY: (userId: string) => `${API_BASE_URL}/profile/counselor/availability/${userId}`,
      STATS: (userId: string) => `${API_BASE_URL}/profile/counselor/stats/${userId}`,
      CONNECTED_STUDENTS: (userId: string) => `${API_BASE_URL}/profile/counselor/connected-students/${userId}`,
      PENDING_REQUESTS: (userId: string) => `${API_BASE_URL}/profile/counselor/pending-requests/${userId}`,
      SESSION_HISTORY: (userId: string) => `${API_BASE_URL}/profile/counselor/session-history/${userId}`,
    },
  },
  PRESENCE: {
    COUNSELOR: {
      STATUS: (counselorId: string) => `${API_BASE_URL}/presence/counselor/${counselorId}/status`,
      UPDATE_STATUS: (counselorId: string) => `${API_BASE_URL}/presence/counselor/${counselorId}/status`,
      ACTIVITY: (counselorId: string) => `${API_BASE_URL}/presence/counselor/${counselorId}/activity`,
      BULK_STATUS: `${API_BASE_URL}/presence/counselors/status`,
      ONLINE_COUNT: `${API_BASE_URL}/presence/counselors/online-count`,
    },
  },
  CHAT: {
    SESSIONS: {
      CREATE: `${API_BASE_URL}/chat/sessions`,
      GET: (id: string) => `${API_BASE_URL}/chat/sessions/${id}`,
      USER_SESSIONS: `${API_BASE_URL}/chat/sessions`,
      END: (id: string) => `${API_BASE_URL}/chat/sessions/${id}/end`,
      MESSAGES: (id: string) => `${API_BASE_URL}/chat/sessions/${id}/messages`,
    },
    MESSAGES: `${API_BASE_URL}/chat/messages`,
  },
  NOTIFICATIONS: {
    LIST: (userId: string) => `${API_BASE_URL}/notifications/${userId}`,
    MARK_READ: (id: string) => `${API_BASE_URL}/notifications/${id}/read`,
    COUNT: (userId: string) => `${API_BASE_URL}/notifications/${userId}/unread-count`,
  },
  VIDEO_CALL: {
    CREATE: `${API_BASE_URL}/video-call/create`,
    JOIN: `${API_BASE_URL}/video-call/join`,
    END: `${API_BASE_URL}/video-call/end`,
    REJECT: `${API_BASE_URL}/video-call/reject`,
    HISTORY: `${API_BASE_URL}/video-call/history`,
    ACTIVE: `${API_BASE_URL}/video-call/active`,
  },
  AGENCY: {
    GET: (agencyId: string) => `${API_BASE_URL}/agency/${agencyId}`,
    UPDATE: (agencyId: string) => `${API_BASE_URL}/agency/${agencyId}`,
    STUDENTS: (agencyId: string) => `${API_BASE_URL}/agencies/${agencyId}/students`,
    COUNSELORS: (agencyId: string) => `${API_BASE_URL}/agencies/${agencyId}/counselors`,
    MY_AGENCY: `${API_BASE_URL}/agencies/my-agency`,
  },
  STORAGE: {
    UPLOAD: `${API_BASE_URL}/storage/upload`,
    DOWNLOAD_URL: (fileId: string) => `${API_BASE_URL}/storage/download-url/${fileId}`,
  },
} as const;

export const COLORS = {
  DEEP_TEAL: "#0891B2",
  SOFT_BLUE: "#E0F2FE",
  WARM_ORANGE: "#F97316",
  SUCCESS_GREEN: "#10B981",
  PURPLE_ACCENT: "#8B5CF6",
  IN_SESSION: "#F59E0B",
  OFFLINE_GRAY: "#6B7280",
} as const;

export const WEBSOCKET_EVENTS = {
  AUTHENTICATE: "authenticate",
  CHAT_MESSAGE: "chat_message",
  TYPING_START: "typing_start",
  TYPING_STOP: "typing_stop",
  GO_LIVE: "go_live",
  GO_OFFLINE: "go_offline",
  NEW_MESSAGE: "new_message",
  MESSAGE_SENT: "message_sent",
  USER_TYPING: "user_typing",
  USER_STOPPED_TYPING: "user_stopped_typing",
  COUNSELOR_LIVE_STATUS_CHANGED: "counselor_live_status_changed",
  CALL_REQUEST: "call_request",
  CALL_ACCEPTED: "call_accepted",
  CALL_REJECTED: "call_rejected",
  CALL_ENDED: "call_ended",
  INCOMING_CALL: "incoming_call",
  CALL_STARTED: "call_started",
  ERROR: "error",
  AUTHENTICATED: "authenticated",
  // Presence events
  PRESENCE_STATUS_CHANGED: "presence_status_changed",
  COUNSELOR_WENT_ONLINE: "counselor_went_online",
  COUNSELOR_WENT_OFFLINE: "counselor_went_offline",
  COUNSELOR_BECAME_BUSY: "counselor_became_busy",
  COUNSELOR_BECAME_AVAILABLE: "counselor_became_available",
  BULK_PRESENCE_UPDATE: "bulk_presence_update",
} as const;